/**
 * PricingRow — Single price-list row in the multi-row Selling Price card.
 *
 * Renders: base price, customer pays, applied rule, other-rules disclosure.
 * Primary row gets blue highlight + "Primary" badge.
 * Non-primary rows are read-only (no edit button) in this epic.
 *
 * Part of Plan 170 (Item Pricing Table Redesign), Epic 02.
 */

import { useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePageTranslation } from "@/i18n/useTranslation";
import { inventoryTranslations as enInventory } from "@/i18n/locales/en/inventory";
import { inventoryTranslations as thInventory } from "@/i18n/locales/th/inventory";
import type {
  PricingTableRow,
  PricingTableOtherRule,
} from "@/features/inventory/api/pricingSummaryHooks";
import { buildDetailRoute } from "@/lib/config/docRoutes";

// ── Currency Formatter ──────────────────────────────────────

function formatAmount(amount: number | null, currency: string): string {
  if (amount == null) return "\u2014";
  try {
    return new Intl.NumberFormat("th-TH", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // Fallback for unknown currency codes
    return `${currency} ${amount.toLocaleString("th-TH", { minimumFractionDigits: 2 })}`;
  }
}

// ── Other Rules Disclosure ──────────────────────────────────

function OtherRulesDisclosure({
  rules,
  priceList,
  t,
}: {
  rules: PricingTableOtherRule[];
  priceList: string;
  t: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);

  if (rules.length === 0) return null;

  const disclosureId = `other-rules-${priceList.replace(/\s+/g, "-").toLowerCase()}`;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 rounded"
        aria-expanded={open}
        aria-controls={disclosureId}
      >
        <ChevronDown
          className={cn(
            "h-3 w-3 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
        <span>{t.pricingOtherRules}</span>
      </button>
      {open && (
        <div
          id={disclosureId}
          className="mt-2 pl-4 space-y-1.5"
          style={{
            animation: "fadeIn 200ms ease-out",
          }}
        >
          {rules.map((rule) => (
            <div
              key={rule.name}
              className="flex items-baseline justify-between text-xs text-muted-foreground/50"
            >
              <span>
                {rule.title} &middot; {rule.display}
              </span>
              <span className="text-xs italic">
                {rule.reason_not_applied === "lower_priority"
                  ? t.pricingNotAppliedLower
                  : t.pricingNotAppliedOverride}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Empty State CTA ─────────────────────────────────────────

function EmptyRowCta({ onSetPrice, t }: { onSetPrice: () => void; t: Record<string, string> }) {
  return (
    <div className="py-6 text-center">
      <p className="text-sm font-medium text-foreground mb-1">
        {t.pricingSetPrice}
      </p>
      <p className="text-xs text-muted-foreground mb-3">
        {t.pricingAddBasePrice}
      </p>
      <Button
        onClick={onSetPrice}
        className="rounded-full min-h-[44px] px-8 bg-primary text-primary-foreground"
      >
        {t.pricingSetSellingPrice}
      </Button>
    </div>
  );
}

// ── Main PricingRow ─────────────────────────────────────────

interface PricingRowProps {
  row: PricingTableRow;
  /** Only defined for the primary row — non-primary rows are read-only */
  onEditBase?: () => void;
}

export function PricingRow({ row, onEditBase }: PricingRowProps) {
  const t = usePageTranslation({ en: enInventory, th: thInventory });
  const isPrimary = row.is_primary;

  // Empty state: no Item Price on this price list
  if (!row.has_item_price && isPrimary && onEditBase) {
    return (
      <div className={cn("px-5 py-2", isPrimary && "bg-sky-50/50")}>
        <RowHeader priceList={row.price_list} isPrimary={isPrimary} t={t} />
        <EmptyRowCta onSetPrice={onEditBase} t={t} />
      </div>
    );
  }

  // Non-primary row with no Item Price — skip rendering entirely
  if (!row.has_item_price) return null;

  return (
    <div className={cn("px-5 py-4", isPrimary && "bg-sky-50/50")}>
      <RowHeader priceList={row.price_list} isPrimary={isPrimary} t={t} />

      {/* Base price */}
      <div className="mt-3">
        <span className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wide">
          {t.pricingBase}
        </span>
        {onEditBase ? (
          <button
            type="button"
            onClick={onEditBase}
            className="block mt-0.5 text-sm tabular-nums text-foreground/70 hover:underline hover:decoration-dotted hover:underline-offset-4 cursor-pointer focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 rounded"
            aria-label={`Base price ${formatAmount(row.base_price, row.currency)}, click to edit`}
          >
            {formatAmount(row.base_price, row.currency)}
          </button>
        ) : (
          <span
            className="block mt-0.5 text-sm tabular-nums text-foreground/70"
            aria-label={`Base price ${formatAmount(row.base_price, row.currency)}`}
          >
            {formatAmount(row.base_price, row.currency)}
          </span>
        )}
      </div>

      {/* Customer pays */}
      <div className="mt-3">
        <span className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wide">
          {t.pricingCustomerPays}
        </span>
        <div className="flex items-baseline gap-2 flex-wrap mt-0.5">
          <span
            className="text-xl font-semibold tabular-nums text-foreground leading-none"
            aria-label={`Customer pays ${formatAmount(row.customer_pays, row.currency)}`}
          >
            {formatAmount(row.customer_pays, row.currency)}
          </span>
          {row.savings > 0 && (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs font-medium tabular-nums text-sky-600 bg-sky-100/60 px-1.5 py-0.5 rounded cursor-default">
                    &minus;{formatAmount(row.savings, row.currency)}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs max-w-xs">
                  {t.pricingBase} {formatAmount(row.base_price, row.currency)}{" "}
                  {row.applied_rule?.display ?? ""} ={" "}
                  {formatAmount(row.customer_pays, row.currency)} ({t.pricingSave}{" "}
                  {formatAmount(row.savings, row.currency)})
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Applied rule */}
      <div className="mt-3">
        <span className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wide">
          {t.pricingAppliedRule}
        </span>
        {row.applied_rule ? (
          <span className="block mt-0.5 text-sm text-foreground/60">
            <a
              href={buildDetailRoute("Pricing Rule", row.applied_rule.name)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sky-700 hover:underline"
              aria-label={`Edit rule: ${row.applied_rule.title}`}
            >
              {row.applied_rule.title}
            </a>
            {" "}
            <span className="text-muted-foreground/50">
              &middot; {row.applied_rule.display}
            </span>
          </span>
        ) : (
          <span className="block mt-0.5 text-sm text-muted-foreground/40">&mdash;</span>
        )}
      </div>

      {/* Other rules disclosure */}
      {/* NOTE: Expired rule styling (epic AC) is deferred — backend only returns active rules */}
      <OtherRulesDisclosure rules={row.other_active_rules} priceList={row.price_list} t={t} />
    </div>
  );
}

// ── Row Header ──────────────────────────────────────────────

function RowHeader({
  priceList,
  isPrimary,
  t,
}: {
  priceList: string;
  isPrimary: boolean;
  t: Record<string, string>;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {/* Status dot */}
        <span
          className={cn(
            "inline-block w-2 h-2 rounded-full shrink-0",
            isPrimary
              ? "bg-sky-500"
              : "border-[1.5px] border-muted-foreground/30"
          )}
          aria-hidden="true"
        />
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-foreground/60">
          {priceList}
        </span>
      </div>
      <span className="text-xs font-medium text-foreground/50">
        {isPrimary ? t.pricingPrimaryOnShop : t.pricingInternal}
      </span>
    </div>
  );
}
